import { useEffect, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { PageHeader } from "@/components/Primitives";
import { refreshBranches } from "@/components/BranchBits";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, MapPin, Crown, Pencil, Trash2, UserCog, Building2, Check } from "lucide-react";
import { toast } from "sonner";

const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

function BranchForm({ open, onClose, editing, onSaved }) {
  const [f, setF] = useState({ name: "", city: "", address: "", phone: "", is_head_office: false });
  useEffect(() => {
    if (open) setF(editing ? { name: editing.name, city: editing.city || "", address: editing.address || "", phone: editing.phone || "", is_head_office: false }
      : { name: "", city: "", address: "", phone: "", is_head_office: false });
  }, [open, editing]);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Branch name is required");
    try {
      if (editing) await api.patch(`/branches/${editing.id}`, { name: f.name, city: f.city, address: f.address, phone: f.phone });
      else await api.post("/branches", f);
      toast.success(editing ? "Branch updated" : "Branch created");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="branch-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit branch" : "New branch"}</DialogTitle>
          <DialogDescription>Branches belong to this company and share its employees, projects and clients.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input data-testid="branch-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Bengaluru Office" /></div>
            <div><Label>City</Label><Input data-testid="branch-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Bengaluru" /></div>
          </div>
          <div><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          {!editing && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="branch-head-office" checked={f.is_head_office} onChange={(e) => setF({ ...f, is_head_office: e.target.checked })} />
              Make this the Head Office <span className="text-xs text-muted-foreground">(the first branch always is)</span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="branch-save" onClick={save}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminsDialog({ open, onClose, branch, employees, onSaved }) {
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  useEffect(() => { if (open && branch) { setSel(new Set((branch.admins || []).map((a) => a.id))); setQ(""); } }, [open, branch]);
  // Company Admins already see everything; people already administering *another* branch are unavailable.
  const pool = employees.filter((e) => e.role !== "admin" && e.role !== "client" && e.is_active !== false
    && !(e.role === "branch_admin" && e.branch_id && e.branch_id !== branch?.id)
    && (!q || e.full_name.toLowerCase().includes(q.toLowerCase())));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    try {
      await api.put(`/branches/${branch.id}/admins`, { employee_ids: Array.from(sel) });
      toast.success("Branch admins updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!branch) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="branch-admins-dialog">
        <DialogHeader>
          <DialogTitle>Branch admins · {branch.name}</DialogTitle>
          <DialogDescription>Selected employees become Branch Admins of this branch: they can view everything and manage the records they create.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Search employees…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" />
        <div className="max-h-64 overflow-y-auto scroll-thin space-y-1">
          {pool.map((e) => (
            <div key={e.id} role="button" onClick={() => toggle(e.id)} data-testid={`ba-pick-${e.id}`}
              className={`px-2 py-1.5 rounded flex items-center gap-2 text-sm cursor-pointer ${sel.has(e.id) ? "bg-primary/15" : "hover:bg-accent"}`}>
              <Avatar className="h-7 w-7"><AvatarImage src={authedUrl(e.avatar_url)} /><AvatarFallback className="text-[9px]">{initials(e.full_name)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0"><div className="truncate">{e.full_name}</div><div className="text-[10px] text-muted-foreground truncate">{e.designation}</div></div>
              {sel.has(e.id) && <Check className="w-4 h-4 text-primary" />}
            </div>
          ))}
          {pool.length === 0 && <div className="text-xs text-muted-foreground p-3 text-center">No eligible employees</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ba-save" onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Branches() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adminsFor, setAdminsFor] = useState(null);

  const load = async () => {
    const data = await refreshBranches();
    setRows(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, []);

  const afterChange = () => { load(); api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); };

  const makeHead = async (b) => {
    try { await api.post(`/branches/${b.id}/head-office`); toast.success(`${b.name} is now the Head Office`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (b) => {
    try { await api.patch(`/branches/${b.id}`, { is_active: b.is_active === false }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete branch "${b.name}"?`)) return;
    try { await api.delete(`/branches/${b.id}`); toast.success("Branch deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="branches-header"
        title="Branches"
        subtitle={`${rows.length} branch${rows.length === 1 ? "" : "es"} · one Head Office, each branch managed by its own Branch Admin`}
        actions={<Button data-testid="new-branch-btn" onClick={() => { setEditing(null); setFormOpen(true); }}><Plus className="w-4 h-4 mr-1" />New Branch</Button>}
      />
      {loading ? <div className="text-muted-foreground p-6">Loading…</div> : rows.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-muted-foreground" data-testid="branches-empty">
          <Building2 className="w-10 h-10 mx-auto opacity-40 mb-2" />
          No branches yet. The first branch you create becomes the Head Office.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((b) => (
            <div key={b.id} data-testid={`branch-${b.id}`} className={`glass rounded-2xl p-5 space-y-3 ${b.is_active === false ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-lg truncate">{b.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{b.city || "—"}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {b.is_head_office && <Badge className="gap-1" data-testid={`ho-${b.id}`}><Crown className="w-3 h-3" />Head Office</Badge>}
                  {b.is_active === false && <Badge variant="outline">Inactive</Badge>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="rounded-lg bg-secondary/50 py-2"><div className="number text-lg">{b.employee_count}</div><div className="text-[10px] label-caps">Employees</div></div>
                <div className="rounded-lg bg-secondary/50 py-2"><div className="number text-lg">{b.project_count}</div><div className="text-[10px] label-caps">Projects</div></div>
              </div>
              <div>
                <div className="label-caps text-[10px] mb-1">Branch Admins</div>
                {(b.admins || []).length === 0 ? <div className="text-xs text-muted-foreground">No branch admin assigned</div> : (
                  <div className="flex flex-wrap gap-1.5">
                    {b.admins.map((a) => (
                      <span key={a.id} className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-primary/10">
                        <Avatar className="h-4 w-4"><AvatarImage src={authedUrl(a.avatar_url)} /><AvatarFallback className="text-[7px]">{initials(a.full_name)}</AvatarFallback></Avatar>{a.full_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 pt-1 flex-wrap">
                <Button size="sm" variant="outline" data-testid={`admins-${b.id}`} onClick={() => setAdminsFor(b)}><UserCog className="w-3.5 h-3.5 mr-1" />Admins</Button>
                <Button size="sm" variant="ghost" data-testid={`edit-branch-${b.id}`} onClick={() => { setEditing(b); setFormOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                {!b.is_head_office && b.is_active !== false && <Button size="sm" variant="ghost" data-testid={`make-ho-${b.id}`} onClick={() => makeHead(b)}><Crown className="w-3.5 h-3.5 mr-1" />Make HO</Button>}
                {!b.is_head_office && <Button size="sm" variant="ghost" onClick={() => toggleActive(b)}>{b.is_active === false ? "Activate" : "Deactivate"}</Button>}
                {!b.is_head_office && <Button size="sm" variant="ghost" className="text-red-500 ml-auto" data-testid={`del-branch-${b.id}`} onClick={() => remove(b)}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <BranchForm open={formOpen} onClose={() => setFormOpen(false)} editing={editing} onSaved={afterChange} />
      <AdminsDialog open={!!adminsFor} onClose={() => setAdminsFor(null)} branch={adminsFor} employees={employees} onSaved={afterChange} />
    </div>
  );
}
